import { useEffect, useRef } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Badge, Button } from "@gradebook/ui";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { holdsAnyRole } from "@/lib/roles";
import { useSelectedAcademicYear } from "@/hooks/useSelectedAcademicYear";
import {
  formatNextSubmissionDeadline,
  formatTermRange,
  termStatus,
} from "@/lib/academicYearTerms";
import type { AcademicYear } from "@/types/api";
import type { BadgeProps } from "@gradebook/ui";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

/** How a year reads on a badge: live, archived, or simply not the live one. */
function yearStatus(year: AcademicYear): { label: string; variant: BadgeVariant } | null {
  if (year.isActive) return { label: "Active", variant: "success" };
  if (year.isArchived) return { label: "Archived", variant: "warning" };
  return null;
}

/**
 * The app-wide academic-year control: a pill on the shell's topbar, and a
 * popover that earns its width by showing what a bare `<select>` of year names
 * cannot — each term's dates, its posting status, and the submission deadline
 * `terms.gradeSubmissionDeadline` already stores and nothing else in the app
 * reads.
 *
 * The year is global (every page is scoped to it); term is page-local and stays
 * in each page's own tabs.
 */
export function AcademicYearMenu() {
  const effectiveRoles = useAuthStore((state) => state.getEffectiveRoles());

  // `/api/academic-years` and `/api/terms` are teacher-or-admin only, so for a
  // student or parent the control could only ever render a 403. The check is
  // here, above the querying component, so those requests are never made.
  //
  // The whole set, not the primary office: a teacher who also holds
  // `coordinator` outranks `teacher` on precedence and would otherwise lose the
  // year pill on every page the API still serves them.
  if (!holdsAnyRole(effectiveRoles, ["admin", "teacher"])) return null;

  return <YearPill />;
}

function YearPill() {
  const { years, selectedYear, selectedTerms, termCounts, selectYear } =
    useSelectedAcademicYear();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // ⌘Y / Ctrl+Y toggles the popover. Routed through the button rather than a
  // second piece of open state so Headless UI keeps owning focus and dismissal.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "y" || !(event.metaKey || event.ctrlKey)) return;
      // Ctrl+Y is redo in a text field; a year switcher has no business
      // stealing it there.
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable='true']")) return;
      event.preventDefault();
      buttonRef.current?.click();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (!selectedYear) return null;

  const status = yearStatus(selectedYear);
  const otherYears = years.filter((year) => year.id !== selectedYear.id);
  const deadline = formatNextSubmissionDeadline(selectedTerms);

  return (
    <Popover className="relative">
      <PopoverButton
        ref={buttonRef}
        aria-label="Academic year"
        className={cn(
          "flex flex-none cursor-pointer items-center gap-1.5 rounded-full border bg-card px-2.5 py-[7px] sm:gap-2 sm:px-3",
          "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          selectedYear.isArchived
            ? "border-accent-600 hover:bg-accent-50"
            : "border-primary-200 hover:bg-primary-50"
        )}
      >
        {/* The eyebrow and the status badge are the pill's widest, least load-
            bearing parts. On a phone the whole topbar is fighting for ~390px,
            so they drop and the year name — the thing the control is for —
            stays. `aria-label` on the button still says what this is. */}
        <span
          className={cn(
            "hidden text-[10px] font-semibold uppercase leading-none tracking-[0.06em] sm:inline",
            selectedYear.isArchived ? "text-accent-700" : "text-primary-400"
          )}
        >
          Year
        </span>
        <span
          className={cn(
            "whitespace-nowrap text-[13px] font-bold leading-none",
            selectedYear.isArchived ? "text-accent-900" : "text-primary-900"
          )}
        >
          {selectedYear.name}
        </span>
        {status && (
          <span className="hidden sm:inline">
            <Badge variant={status.variant} size="sm">
              {status.label}
            </Badge>
          </span>
        )}
        <ChevronDownIcon
          className={cn(
            "size-3.5 flex-none",
            selectedYear.isArchived ? "text-accent-700" : "text-primary-600"
          )}
        />
      </PopoverButton>

      <PopoverPanel
        role="dialog"
        aria-label="Switch academic year"
        // The panel hangs off the pill's right edge, and the pill is not flush
        // with the screen — the avatar sits to its right. So the width has to
        // leave room for everything right of the pill too, or the panel runs
        // off the *left* side of a phone.
        className="absolute right-0 top-11 z-30 w-[min(346px,calc(100vw-72px))] overflow-hidden rounded-[18px] border bg-card shadow-[0_16px_40px_rgba(0,0,0,0.16)]"
      >
        {({ close }) => (
          <>
            <div className="flex items-baseline justify-between border-b border-neutral-100 px-3.5 py-[11px]">
              <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.07em] text-muted-foreground">
                Switch academic year
              </span>
              <span className="font-mono text-[10px] font-medium text-neutral-400">⌘Y</span>
            </div>

            <div className="p-1.5">
              {/* The year in scope, expanded: its terms are the reason this is a
                  popover and not a dropdown. */}
              <div className="rounded-[14px] border border-primary-200 bg-primary-50 px-[11px] py-2.5">
                <div className="flex items-center gap-[7px]">
                  <span className="text-[13.5px] font-bold text-primary-900">
                    {selectedYear.name}
                  </span>
                  {status && (
                    <Badge variant={status.variant} size="sm">
                      {status.label}
                    </Badge>
                  )}
                </div>

                <div className="mt-2 flex flex-col gap-[5px]">
                  {selectedTerms.map((term) => {
                    const termBadge = termStatus(term);
                    return (
                      <div key={term.id} className="flex items-center gap-[7px] text-[11px]">
                        <span className="w-[46px] shrink-0 font-semibold text-primary-900">
                          {term.termName}
                        </span>
                        <span className="text-muted-foreground">{formatTermRange(term)}</span>
                        <span className="ml-auto">
                          <Badge variant={termBadge.variant} size="sm">
                            {termBadge.label}
                          </Badge>
                        </span>
                      </div>
                    );
                  })}
                  {selectedTerms.length === 0 && (
                    <span className="text-[11px] text-muted-foreground">
                      No terms set up for this year yet.
                    </span>
                  )}
                  {deadline && (
                    <div className="mt-0.5 text-[10.5px] text-primary-700">{deadline}</div>
                  )}
                </div>
              </div>

              {otherYears.map((year) => {
                const otherStatus = yearStatus(year);
                const count = termCounts[year.id] ?? 0;
                return (
                  <Button
                    key={year.id}
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      selectYear(year.id);
                      close();
                    }}
                    className="mt-0.5 flex h-auto w-full items-center justify-start gap-[7px] rounded-[14px] px-[11px] py-[9px] text-left font-normal hover:bg-muted"
                  >
                    <span
                      className={cn(
                        "text-[12.5px]",
                        year.isArchived
                          ? "font-medium text-muted-foreground"
                          : "font-semibold text-foreground"
                      )}
                    >
                      {year.name}
                    </span>
                    {otherStatus && (
                      <Badge variant={otherStatus.variant} size="sm">
                        {otherStatus.label}
                      </Badge>
                    )}
                    <span className="ml-auto text-[10.5px] text-muted-foreground">
                      {/* "read-only" describes a year that is not the school's
                          live one. Hanging it off "not currently selected"
                          would label the active year read-only the moment you
                          are looking at a past one — the exact year you can
                          still edit. */}
                      {year.isArchived
                        ? "read-only"
                        : `${count} ${count === 1 ? "term" : "terms"}${
                            year.isActive ? "" : " · read-only"
                          }`}
                    </span>
                  </Button>
                );
              })}
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}
